import React, { useState, useCallback } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

type Season = 'summer' | 'autumn' | 'winter' | 'spring';

interface SeasonTogglesProps {
  width?: number;
  height?: number;
}

export const SeasonToggles: React.FC<SeasonTogglesProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);
  const [season, setSeason] = useState<Season>('summer');

  const seasonData = {
    summer: { angle: 0, text: 'June 21 — 15 hours of daylight', glow: '#EA580C', tilt: 23.5 },
    autumn: { angle: Math.PI / 2, text: 'September 22 — 12 hours of daylight', glow: '#FDE047', tilt: 0 },
    winter: { angle: Math.PI, text: 'December 21 — 9 hours of daylight', glow: '#60A5FA', tilt: -23.5 },
    spring: { angle: Math.PI * 1.5, text: 'March 20 — 12 hours of daylight', glow: '#4ADE80', tilt: 0 },
  };

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;
    const orbitRadius = size * 0.35;

    // Draw Orbit
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.arc(centerX, centerY, orbitRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Draw Sun
    ctx.save();
    const sunGrad = ctx.createRadialGradient(centerX, centerY, 5, centerX, centerY, 30);
    sunGrad.addColorStop(0, '#FFFFFF');
    sunGrad.addColorStop(0.3, '#FDE047');
    sunGrad.addColorStop(1, 'rgba(253, 224, 71, 0)');
    ctx.fillStyle = sunGrad;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 30, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Calculate target Earth position
    const target = seasonData[season];
    const earthX = centerX + Math.cos(target.angle) * orbitRadius;
    const earthY = centerY + Math.sin(target.angle) * orbitRadius;
    const earthRadius = 15;

    // Draw light rays from Sun to Earth
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(253, 224, 71, 0.3)';
    ctx.lineWidth = 2;
    // Draw a cone of light
    const coneAngle = 0.2;
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(earthX + Math.cos(target.angle - coneAngle)*earthRadius, earthY + Math.sin(target.angle - coneAngle)*earthRadius);
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(earthX + Math.cos(target.angle + coneAngle)*earthRadius, earthY + Math.sin(target.angle + coneAngle)*earthRadius);
    ctx.stroke();
    
    // Draw Earth
    ctx.save();
    ctx.translate(earthX, earthY);
    
    // Base Earth
    ctx.beginPath();
    ctx.fillStyle = '#4A90D9';
    ctx.arc(0, 0, earthRadius, 0, Math.PI * 2);
    ctx.fill();
    
    // Landmass mock
    ctx.fillStyle = '#22C55E';
    ctx.beginPath();
    ctx.arc(-5, -5, 6, 0, Math.PI*2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(5, 4, 7, 0, Math.PI*2);
    ctx.fill();

    // Tilt Axis
    ctx.rotate((target.tilt * Math.PI) / 180);
    ctx.beginPath();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    ctx.setLineDash([2, 2]);
    ctx.moveTo(0, -earthRadius - 10);
    ctx.lineTo(0, earthRadius + 10);
    ctx.stroke();
    ctx.setLineDash([]);
    
    // Equator
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = 1;
    ctx.moveTo(-earthRadius, 0);
    ctx.lineTo(earthRadius, 0);
    ctx.stroke();

    // Hemisphere Glow
    ctx.beginPath();
    ctx.fillStyle = target.glow;
    ctx.globalAlpha = 0.3;
    // North hemisphere highlight
    ctx.arc(0, 0, earthRadius, Math.PI, 0);
    ctx.fill();

    ctx.restore();

    // Dark side (Shadow)
    // Draw a half-circle shadow facing away from the sun
    ctx.save();
    ctx.translate(earthX, earthY);
    ctx.rotate(target.angle);
    ctx.beginPath();
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.arc(0, 0, earthRadius, -Math.PI/2, Math.PI/2);
    ctx.fill();
    ctx.restore();

  }, [size, season]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size - 80, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
        <canvas ref={canvasRef} />
      </div>
      
      <div style={{ marginTop: '12px', width: '100%', textAlign: 'center' }}>
        <div style={{ marginBottom: '12px', fontSize: '14px', color: '#E2E8F0', fontWeight: 'bold' }}>
          {seasonData[season].text}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <button 
            onClick={() => setSeason('summer')}
            style={{ padding: '6px', fontSize: '12px', borderRadius: '4px', background: season === 'summer' ? '#EA580C' : '#334155', color: 'white', border: 'none', cursor: 'pointer' }}>
            Summer Solstice
          </button>
          <button 
            onClick={() => setSeason('winter')}
            style={{ padding: '6px', fontSize: '12px', borderRadius: '4px', background: season === 'winter' ? '#60A5FA' : '#334155', color: 'white', border: 'none', cursor: 'pointer' }}>
            Winter Solstice
          </button>
          <button 
            onClick={() => setSeason('spring')}
            style={{ padding: '6px', fontSize: '12px', borderRadius: '4px', background: season === 'spring' ? '#4ADE80' : '#334155', color: 'white', border: 'none', cursor: 'pointer' }}>
            Spring Equinox
          </button>
          <button 
            onClick={() => setSeason('autumn')}
            style={{ padding: '6px', fontSize: '12px', borderRadius: '4px', background: season === 'autumn' ? '#FDE047' : '#334155', color: season === 'autumn' ? 'black' : 'white', border: 'none', cursor: 'pointer' }}>
            Autumn Equinox
          </button>
        </div>
      </div>
    </div>
  );
};
